import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  FaClock,
  FaEnvelope,
  FaPaperPlane,
  FaPen,
  FaSearch,
  FaShieldAlt,
  FaTrash
} from 'react-icons/fa';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Input,
  PageHeader,
  Toggle
} from '../shared/ui';
import Modal from '../components/Modal';
import UserForm from '../components/UserForm';
import { PopupContext } from '../App';
import { API_URL } from '../config/env';
import styles from './UsersPage.module.css';

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
  { id: 'pending', label: 'Pending invite' },
  { id: 'expired', label: 'Expired invite' }
];

function authHeaders(extra = {}) {
  return {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    ...extra
  };
}

function getInitials(name, email) {
  const source = (name || email || '').replace(/@.*$/, '');
  if (!source) return '?';
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return source.charAt(0).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatRelative(future) {
  if (!future) return null;
  const ms = new Date(future).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  if (ms <= 0) return 'expired';
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `in ${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `in ${hours}h`;
  const days = Math.round(hours / 24);
  return `in ${days}d`;
}

function deriveStatus(u) {
  if (u.is_pending && u.invite_expired) return 'expired';
  if (u.is_pending) return 'pending';
  if (u.is_active === false) return 'inactive';
  return 'active';
}

const STATUS_BADGE = {
  active: { variant: 'success', label: 'Active' },
  inactive: { variant: 'neutral', label: 'Inactive' },
  pending: { variant: 'info', label: 'Pending' },
  expired: { variant: 'danger', label: 'Expired' }
};

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('add');
  const [selectedUser, setSelectedUser] = useState(null);
  const showPopup = useContext(PopupContext);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const refresh = () => {
    setLoading(true);
    fetch(`${API_URL}/api/users`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => {
        const arr = Array.isArray(data) ? data : data?.users || [];
        setUsers(arr);
      })
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  const flash = (message, kind = 'success') => {
    showPopup(message, kind);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      const status = deriveStatus(u);
      if (filter !== 'all' && status !== filter) return false;
      if (!q) return true;
      return (
        (u.full_name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.role || '').toLowerCase().includes(q)
      );
    });
  }, [users, search, filter]);

  const counts = useMemo(() => {
    const c = { all: users.length, active: 0, inactive: 0, pending: 0, expired: 0 };
    users.forEach((u) => {
      c[deriveStatus(u)] += 1;
    });
    return c;
  }, [users]);

  const openEdit = (u) => {
    setModalType('edit');
    setSelectedUser({
      ...u,
      name: u.full_name,
      active: u.is_active
    });
    setModalOpen(true);
  };
  const openInvite = () => {
    setModalType('invite');
    setSelectedUser(null);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setSelectedUser(null);
  };

  const handleEdit = async (form) => {
    const res = await fetch(`${API_URL}/api/users/${selectedUser.id}`, {
      method: 'PUT',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        full_name: form.name,
        role: form.role,
        is_active: form.active
      })
    });
    if (res.ok) {
      flash('User updated');
      closeModal();
      refresh();
    } else {
      flash('Failed to update user', 'error');
    }
  };

  const handleInvite = async (form) => {
    if (!form.email) return;
    const res = await fetch(`${API_URL}/api/users/invite`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ email: form.email, full_name: form.name, role: form.role })
    });
    const body = await res.json().catch(() => ({}));
    if (res.ok) {
      flash(`Invitation sent to ${form.email}`);
      closeModal();
      refresh();
    } else {
      const msg =
        body.message ||
        (res.status === 409 ? 'That email is already in use' : 'Failed to send invitation');
      flash(body.detail ? `${msg} (${body.detail})` : msg, 'error');
    }
  };

  const toggleActive = async (u) => {
    const res = await fetch(`${API_URL}/api/users/${u.id}/status`, {
      method: 'PATCH',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ active: !u.is_active })
    });
    if (res.ok) {
      flash(`User ${u.is_active ? 'deactivated' : 'activated'}`);
      refresh();
    } else {
      flash('Failed to update status', 'error');
    }
  };

  const resendInvite = async (u) => {
    const res = await fetch(`${API_URL}/api/users/${u.id}/resend-invite`, {
      method: 'POST',
      headers: authHeaders()
    });
    const body = await res.json().catch(() => ({}));
    if (res.ok) {
      flash(`Invitation resent to ${u.email}`);
      refresh();
    } else {
      const msg = body.message || 'Failed to resend invitation';
      flash(body.detail ? `${msg} (${body.detail})` : msg, 'error');
    }
  };

  const removeUser = async () => {
    if (!confirmDelete) return;
    const res = await fetch(`${API_URL}/api/users/${confirmDelete.id}`, {
      method: 'DELETE',
      headers: authHeaders()
    });
    if (res.ok) {
      flash('User deleted');
      setConfirmDelete(null);
      refresh();
    } else {
      flash('Failed to delete user', 'error');
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Team"
        title="Manage users"
        description="Invite teammates, control access, and watch invitations expire."
        actions={
          <Button iconLeft={<FaPaperPlane />} onClick={openInvite}>
            Invite user
          </Button>
        }
      />

      <div className={styles.page}>
        <div className={styles.toolbar}>
          <div className={styles.search}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, or role"
              iconLeft={<FaSearch />}
            />
          </div>
          <div className={styles.filters} role="tablist">
            {STATUS_FILTERS.map((f) => {
              const selected = filter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={[styles.filterChip, selected ? styles.filterChipSelected : ''].join(' ')}
                  onClick={() => setFilter(f.id)}
                >
                  <span>{f.label}</span>
                  <span className={styles.filterCount}>{counts[f.id] ?? 0}</span>
                </button>
              );
            })}
          </div>
        </div>

        <Card className={styles.tableCard}>
          <CardBody className={styles.tableBody}>
            {loading ? (
              <div className={styles.skeleton}>
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className={styles.skeletonRow} style={{ animationDelay: `${i * 70}ms` }} />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className={styles.empty}>
                <h3>No users match these filters.</h3>
                <p>Try clearing the search or invite someone new.</p>
              </div>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Invite</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((u) => {
                      const status = deriveStatus(u);
                      const badge = STATUS_BADGE[status];
                      const expiresLabel = u.invite_expires_at ? formatRelative(u.invite_expires_at) : null;
                      return (
                        <tr key={u.id}>
                          <td>
                            <div className={styles.userCell}>
                              <span className={styles.avatar} aria-hidden="true">
                                {getInitials(u.full_name, u.email)}
                              </span>
                              <span className={styles.userText}>
                                <span className={styles.userName}>{u.full_name || '—'}</span>
                                <span className={styles.userEmail}>
                                  <FaEnvelope /> {u.email}
                                </span>
                              </span>
                            </div>
                          </td>
                          <td>
                            <Badge
                              variant={u.role === 'Admin' ? 'brand' : 'neutral'}
                              icon={u.role === 'Admin' ? <FaShieldAlt /> : null}
                            >
                              {u.role || 'User'}
                            </Badge>
                          </td>
                          <td>
                            {u.is_pending ? (
                              <Badge variant={badge.variant}>{badge.label}</Badge>
                            ) : (
                              <div className={styles.statusToggleCell}>
                                <Toggle
                                  size="sm"
                                  checked={!!u.is_active}
                                  onChange={() => toggleActive(u)}
                                  ariaLabel={u.is_active ? 'Deactivate user' : 'Activate user'}
                                />
                                <span
                                  className={[
                                    styles.statusLabel,
                                    u.is_active ? styles.statusOn : styles.statusOff
                                  ].join(' ')}
                                >
                                  {u.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            )}
                          </td>
                          <td>
                            {u.is_pending ? (
                              <span
                                className={[
                                  styles.invite,
                                  status === 'expired' ? styles.inviteExpired : ''
                                ].join(' ')}
                              >
                                <FaClock />
                                {status === 'expired'
                                  ? 'Expired'
                                  : expiresLabel
                                  ? `Expires ${expiresLabel}`
                                  : 'No expiry'}
                              </span>
                            ) : (
                              <span className={styles.muted}>—</span>
                            )}
                          </td>
                          <td className={styles.actionsCell}>
                            <div className={styles.rowActions}>
                              {u.is_pending ? (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  iconLeft={<FaPaperPlane />}
                                  onClick={() => resendInvite(u)}
                                >
                                  Resend
                                </Button>
                              ) : null}
                              <Button
                                size="sm"
                                variant="ghost"
                                iconLeft={<FaPen />}
                                onClick={() => openEdit(u)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                iconLeft={<FaTrash />}
                                onClick={() => setConfirmDelete(u)}
                                className={styles.dangerAction}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={modalType === 'edit' ? 'Edit user' : 'Invite user'}
        description={
          modalType === 'invite'
            ? 'They will receive an email link to set their password. Invitations expire in 7 days.'
            : undefined
        }
        size="sm"
      >
        {modalType === 'edit' && selectedUser && (
          <UserForm
            open={modalOpen}
            onClose={closeModal}
            onSubmit={handleEdit}
            initial={selectedUser}
          />
        )}
        {modalType === 'invite' && (
          <UserForm
            open={modalOpen}
            onClose={closeModal}
            onSubmit={handleInvite}
            initial={{ name: '', email: '', role: 'User', active: false }}
            showActive={false}
            submitLabel="Send invite"
          />
        )}
      </Modal>

      <Modal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Delete user"
        description={
          confirmDelete
            ? `Are you sure you want to delete ${confirmDelete.full_name || confirmDelete.email}? This cannot be undone.`
            : ''
        }
        size="sm"
      >
        <div className={styles.confirmActions}>
          <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" iconLeft={<FaTrash />} onClick={removeUser}>
            Delete user
          </Button>
        </div>
      </Modal>
    </>
  );
}
